import { monsterPickDistance } from '../world/monster-shape.js';
import {keepVistaBox} from '../world/keep-vista.js';
import { KEEP_STAIRS, KEEP_DECK_HEIGHT } from '../world/keep-deck.js';
/**
 * 입력과 타겟팅.
 *
 * ★ 강제 공격이 필요한 이유
 *   일반 클릭은 "가장 가까운 것" 을 고른다. 그러면 NPC 옆이나 아군과 겹친 자리에서는
 *   상대 플레이어를 **때릴 방법 자체가 없다.** PK 가 있는 게임에서 이건 기능 부재가 아니라
 *   설계 구멍이다. 그래서 클릭을 두 종류로 나눈다.
 *
 *   일반 클릭 : NPC 대화 · 몬스터 공격 · 이동 · (안전한 땅) 사람을 누르면 이름표 카드
 *   강제 공격 : Ctrl(또는 Shift) + 클릭, 또는 강공 모드 토글
 *               → NPC 를 무시하고, 플레이어도 대상으로 잡는다
 *
 * R227(레드팀 P2-3, DESIGN 14.417) — **한 몸짓에 두 뜻을 싣지 않는다.** 전에는 같은 Ctrl+클릭·강공이 마을 안에서는 거래 신청,
 * 마을 밖에서는 PK 였고, 켜 둔 강공은 마을을 나가도 안 풀려 거래하던 손으로 사냥터의 파티원을 쳤다. 이제
 *   ① 거래는 강공에서 떼어 낸다 — 안전한 땅(마을·전초지·내 길드 영지)에서 사람을 누르면(일반 탭이든 강공이든) 이름표 카드
 *      (`ui/panels/person.js` — [거래]·[파티]·[귓말])가 뜬다. 강공 몸짓은 어디서도 거래를 보내지 않는다.
 *   ② 강공은 안전한 땅에 들어오면 저절로 꺼지고, 켠 채 그 땅을 나가면 기록 한 줄 「강공 켜짐 — 사람을 누르면 공격한다」.
 *   ③ 강공 중에도 안전한 땅의 NPC·게시판·고블린 상인은 누를 수 있다(허수아비·샌드백은 여전히 건너뛴다 — 칠 수 있는 고정물이다).
 * 안전한 땅 밖(사냥터·정착지 밖 NPC 곁)은 전과 같다 — 강공은 NPC 를 건너뛰고 사람을 겨눈다(위 ★).
 */
import { S, $ } from './state.js';
import { ACTION_KEYS, REPEATABLE_KEYS, keyCap } from './keymap.js';
import { toast } from '../ui/toast.js';
import { log } from '../ui/hud.js';
import { where } from '../world/places.js';
import { send, on, nativeActionAllowed } from './net.js';
import { TILE, TOWN_X, TOWN_Y, NPCS, TOWN_DECORATIONS, NPC_TALK_RANGE, npcApproachPoint, settlementAt, guildEstateAt, guildEstateOutside, CAVE_MOUTHS, PORTAL_R, floorAt } from '../world/map.js';
import { goblinAt, goblinPos } from '../world/goblin.js';
import { SCARECROWS } from '../world/scarecrow.js';
import { SANDBAGS } from '../world/dps.js';
import { lairFloorAt } from '../world/lair-map.js';
import { NATIVE_VILLAGE, nativeVillageGateOutside } from '../world/native-village.js';
import { nativeFieldGeometry, nativeFieldFloor } from '../world/native-field.js';
// Q3 ART-R4 1단계 — 쿼터뷰가 켜진 프레임이면 클릭·터치를 역투영한다(화면 위 = 월드 북서). 꺼져 있으면 탑다운 식 그대로다.
import { isoView, isoPickDist, isoPickAt, isoOnScreen, ISO_LIFT } from '../render/iso-scene.js';
import { isoUnproject } from '../render/iso-art.js';
import { predictAttack, noteTap } from '../render/combat-predict.js';

let cv = null;
/**
 * MV1(DESIGN 14.469) — 누르고 걷기(화면 표현 상수 — 콘솔 값 없음, CF1 의 `SMOOTH_*` 와 같은 관례).
 * 이만큼(ms) 넘게 누르고 있거나 화면에서 `DRAG_PX`(CSS px) 넘게 끌면 탭이 아니라 누르고 걷기다. 걷는 동안 목표는 커서 방향으로
 * 내 몸 앞 `DRAG_LEAD` 칸 이상(약 0.4초 걸음), 커서가 화면 가운데 `DRAG_DEAD` 칸 안이면 선다.
 */
const HOLD_MS = 120, DRAG_PX = 10, DRAG_LEAD = 1.6, DRAG_DEAD = 0.35;
function overKeepVista(x,y){const b=keepVistaBox(innerWidth,innerHeight,S.you);return b && x>=b.x && x<=b.x+b.w && y>=b.y && y<=b.y+b.h;}

export function screenToWorld(px, py) {
  if (isoView.active) return isoUnproject(isoView, px, py);
  return {
    x: S.cam.x + (px - innerWidth / 2) / TILE,
    y: S.cam.y + (py - innerHeight / 2) / TILE + (S.you?.keepDeck ? KEEP_DECK_HEIGHT : 0),
  };
}

/**
 * R227 — **사람을 칠 수 없는 땅인가**(마을·전초지·내 길드 영지 — 서버 `pkAllowed` 가 앞쪽에서 빼는 정착지·영지와 같은 자리).
 * 사냥터 소켓 위는 늘 아니다(`where` 가 `'out'`). 정착지 밖 NPC 곁(`'service'`)도 아니다 — PK 구역일 수 있어 강공이 NPC 를
 * 건너뛰어야 그 곁의 사람을 겨눌 수 있다.
 */
export function safeGround(you = S.you) {
  return !!you && !nativeFieldGeometry() && ['town', 'outpost', 'estate'].includes(where(you));
}

/**
 * 커서 아래에 무엇이 있나. force 면 NPC 를 건너뛰고 플레이어를 포함한다(R227 ③ — 안전한 땅에서는 강공도 NPC 를 고른다).
 * ⚠ 검사기(field-dragon-probe)가 이 선언만 떼어 import 만 이어 돌린다 — 모듈 안 도우미(`safeGround` 등)를 부르지 않는다.
 *   그래서 안전한 땅 판정은 위 `safeGround` 와 **같은 식**을 여기 한 줄로 둔다.
 */
export function pick(wx, wy, force) {
  let best = null, bd = 1.0;
  const safe = !!S.you && !nativeFieldGeometry() && ['town', 'outpost', 'estate'].includes(where(S.you));
  /** 사람 몸까지의 누르기 거리 — 쿼터뷰면 몸 가운데까지 들어 올려 잰다(몬스터·NPC 와 같은 식) */
  const personDist = (p) => isoView.active ? isoPickAt(p, wx, wy, 'human')   // 갑판 층 들어 올림(14.419)
    : Math.hypot((p.viewX ?? p.x) - wx, (p.viewY ?? p.y) + ((S.you?.keepDeck?KEEP_DECK_HEIGHT:0) - (p.keepDeck?KEEP_DECK_HEIGHT:0)) - wy);
  /** R227 ① — 일반 탭으로 사람(원주민이 아닌)을 고르는 반경. 원주민(0.55)보다 조금 넓다 — 마을엔 몬스터가 없어 잘못 눌러도 카드 하나다 */
  const PERSON_PICK = 0.6;
  if ((!force || safe) && !nativeFieldGeometry()) {
    if(S.keepOpen && Math.hypot(KEEP_STAIRS.x-wx,KEEP_STAIRS.y-wy)<1.1)return {kind:'keepStairs',o:KEEP_STAIRS};
    /**
     * 고블린 상인이 **맨 먼저**다 — 잠깐만 떠 있고, 뜬 동안 누르는 사람은 그것을 노린다.
     *
     * 전에는 게시판·NPC 뒤였는데, 이 사슬은 거리 비교가 아니라 층층이 `if (best) return best`
     * 라서 **게시판이나 텔레포터가 하나라도 걸리면 고블린은 아무리 가까워도 안 집혔다.**
     * 게시판 히트박스는 위로 1.2타일까지 넓어서, 마을 게시판 옆에 뜬 고블린은 사실상 못 눌렀다
     * (2026-09-16 운영 신고: *"여기 나오면 클릭하기 엄청 힘들긴해요...텔포 눌리거나 게시판 눌림"*).
     * 게시판·텔레포터는 늘 그 자리에 있어 한 걸음 옆에서 다시 누르면 되지만, 고블린은 사라진다.
     */
    {
      const gv = goblinAt(Date.now() / 1000);
      if (gv) {
        const gp = goblinPos(gv);
        if (Math.hypot(gp.x - wx, gp.y + (S.you?.keepDeck?KEEP_DECK_HEIGHT:0) - wy) < bd)
          return { kind: 'goblin', o: { ...gv, x: gp.x, y: gp.y } };
      }
    }
    for (const b of TOWN_DECORATIONS) {
      if (b.kind !== 'board') continue;
      // 그림은 타일 위로 솟으므로 발밑 사각형보다 조금 넓게 누를 수 있게 한다.
      const dx = Math.max(b.x - 0.2 - wx, 0, wx - (b.x + b.w + 0.2));
      const dy = Math.max(b.y - 1.2 - wy, 0, wy - (b.y + b.h + 0.2));
      const d = Math.hypot(dx, dy);
      if (d < bd) { bd = d; best = { kind: 'board', o: b }; }
    }
    if (best) return best;
    for (const n of NPCS) {
      const d = isoView.active ? isoPickDist(TOWN_X + n.dx, TOWN_Y + n.dy, wx, wy, ISO_LIFT.human) : Math.hypot(TOWN_X + n.dx - wx, TOWN_Y + n.dy - wy);
      if (d < bd) { bd = d; best = { kind: 'npc', o: n }; }
    }
    if (best) return best;
    /**
     * 허수아비는 **NPC 와 같은 층**이다 — 마을 고정물이라 몬스터보다 먼저 본다.
     * `force`(강공) 일 때는 건너뛴다: 강공은 사람을 겨누는 몸짓이고,
     * 그 와중에 허수아비가 잡히면 **때리려던 사람 대신 허수아비를 친다.**
     * (R227 ③ — 강공이어도 안전한 땅이면 위 NPC·게시판·고블린까지는 고르고, 칠 수 있는 고정물인 여기부터는 여전히 건너뛴다.)
     */
    if (!force) {
      for (const d of SCARECROWS) {
        const dd = Math.hypot(d.x - wx, d.y - wy);
        if (dd < bd) { bd = dd; best = { kind: 'dummy', o: d }; }
      }
      if (best) return best;
      /**
       * UNI-Q161 샌드백 — 허수아비와 **같은 층**이다(마을 고정물). 강공일 때 건너뛰는
       * 이유도 같다: 사람을 겨눈 몸짓에 고정물이 잡히면 **때리려던 사람 대신** 그것을 친다.
       */
      for (const b of SANDBAGS) {
        const dd = Math.hypot(b.x - wx, b.y - wy);
        if (dd < bd) { bd = dd; best = { kind: 'sandbag', o: b }; }
      }
      if (best) return best;
    }
  }
  for (const m of S.mons) {
    // 쿼터뷰 — 누르는 것은 발이 아니라 몸이다(몸 가운데까지 들어 올려 잰다 · 갑판 층·공성 구조물·종말 드래곤은 isoPickAt, 14.419)
    const d = isoView.active ? isoPickAt(m, wx, wy, 'mon')
      : monsterPickDistance(m, wx - (m.viewX ?? m.x), wy - (m.viewY ?? m.y) - ((S.you?.keepDeck?KEEP_DECK_HEIGHT:0) - (m.keepDeck?KEEP_DECK_HEIGHT:0)), TILE);
    if (d < bd) { bd = d; best = { kind: 'mon', o: m }; }
  }
  // GUARD-R2 — 경비병. 강공 없이 집힌다(국가 상대 몸짓은 PK 가 아니다)
  for (const g of (S.guards || [])) {
    // UNI-Q192 — 보디가드: 내 보디가드는 안 집힌다(누르면 그 자리로 걷는다). 남의 보디가드는 사람을 겨누는 강공으로만
    if (g.bg && (g.mine || !force)) continue;
    const d = isoView.active ? isoPickAt(g, wx, wy, 'human')
      : Math.hypot((g.viewX ?? g.x) - wx, (g.viewY ?? g.y) + ((S.you?.keepDeck?KEEP_DECK_HEIGHT:0) - (g.keepDeck?KEEP_DECK_HEIGHT:0)) - wy);
    if (d < bd) { bd = d; best = { kind: 'guard', o: g }; }
  }
  if (force) {
    for (const p of S.players) {
      if (p.d) continue;
      const d = personDist(p);
      if (d < bd) { bd = d; best = { kind: 'player', o: p }; }
    }
  }
  // Ordinary taps inspect wandering residents; forced player targeting keeps its existing meaning.
  // R227 ① — 안전한 땅에서는 원주민이 아닌 사람도 일반 탭으로 고른다(이름표 카드). 사냥터·PK 땅에서는 전처럼 누른 자리로 걷는다.
  if (!force && !best) {
    for (const p of S.players) {
      // R257 — 원주민은 소속 카드(`resident`)로, 사냥터에서 카드가 늦으면 몸 id(`wanderer:…`)로 안다
      const resident = !!p.resident || String(p.id).startsWith('wanderer:');
      // R227 ① — 원주민이 아닌 사람은 안전한 땅에서만(쓰러진 사람은 빼고) 이름표 카드
      if (!resident && (!safe || p.d)) continue;
      const d = personDist(p);
      if (d < Math.min(bd, resident ? 0.55 : PERSON_PICK)) { bd=d; best={kind:resident ? 'resident' : 'person',o:p}; }
    }
  }
  if (!best) {
    for (const d0 of S.drops) {
      if (Math.hypot(d0.x - wx, d0.y + ((S.you?.keepDeck?KEEP_DECK_HEIGHT:0) - (d0.keepDeck?KEEP_DECK_HEIGHT:0)) - wy) < 0.7) return { kind: 'drop', o: d0 };
    }
  }
  return best;
}

/**
 * R207(레드팀 D2-6) — **먼 NPC 를 누르면 걸어가서, 도착하면 창을 연다.** 전에는 다가가는 이동만 보내고 끝나
 * 도착해도 아무 일이 없었다 — 「고장인가?」 하고 한 번 더 눌러야 열렸고, 폰에서는 작은 NPC 를 두 번째로 누르다 자주 빗나갔다.
 * 창을 여는 것은 화면 일이다(서버 판정은 그대로 — 창 안의 행동은 서버가 거리·정착지를 다시 본다). 다른 곳을 누르거나
 * 끌어서 움직이거나, 그사이 다른 창을 열거나, 죽거나, 12초 안에 못 닿으면 잊는다.
 */
const TALK_WAIT_MS = 12000;
let pendingTalk = null;
function queueTalk(reach, open, target) {
  pendingTalk = { reach, open, target, panelAtTap: S.panel ?? null, at: performance.now() };
}
function cancelTalk() { pendingTalk = null; }
/** 대화 거리 안이고 같은 정착지인가 — 누른 순간의 판정과 같은 식이다(게시판은 정착지가 없어도 같아야 한다) */
function talkReach(x, y, strict) {
  if (!S.you) return false;
  const home = settlementAt(x, y, 2), here = settlementAt(S.you.x, S.you.y, 2);
  if (Math.hypot(x - S.you.x, y - S.you.y) > NPC_TALK_RANGE) return false;
  return strict ? here?.id === home?.id : !(home && here?.id !== home.id);
}
/** 상태가 올 때마다 — 닿았으면 연다 */
function checkPendingTalk() {
  const p = pendingTalk;
  if (!p) return;
  const otherPanel = S.panel && S.panel !== p.panelAtTap && S.panel !== p.target;
  if (!S.you || S.you.dead || otherPanel || performance.now() - p.at > TALK_WAIT_MS) { pendingTalk = null; return; }
  if (!p.reach()) return;
  pendingTalk = null;
  p.open();
}

/**
 * Z1(평론 #6) — **손가락 누름 너그러움.** 마우스 반경(1칸 — 폰에서 약 15px)으로는 작은 몬스터를 자주 놓쳐 그 자리로 걸어갔다.
 * 손가락으로 누른 자리에 아무것도 안 걸리면 이 반경(칸) 안의 가장 가까운 몬스터를 친다(권장 누름 크기 44pt 쯤 — 폰 가로 한 칸 약 24px).
 * 칠 수 있는지·사거리는 서버가 다시 본다.
 */
const TOUCH_PICK = 1.7;   // ⚠ `export const` 로 두지 않는다 — 검사기(readability-probe keyboardProbe)가 `export function` 만 벗겨 이 파일을 돈다
export function touchMonster(wx, wy, reach = TOUCH_PICK) {
  let best = null, bd = reach;
  for (const m of S.mons) {
    if (m.sg) continue;   // 공성 구조물은 제 모양으로만(`isoSiegePick`) — 끌어당기지 않는다
    if (!isoOnScreen(m.viewX ?? m.x, m.viewY ?? m.y, 0)) continue;   // C6(14.476) — 화면 가장자리 탭이 화면 밖 몬스터를 끌어당기지 않는다
    const d = isoView.active ? isoPickAt(m, wx, wy, 'mon') : Math.hypot((m.viewX ?? m.x) - wx, (m.viewY ?? m.y) - wy);
    if (d < bd) { bd = d; best = { kind: 'mon', o: m }; }
  }
  return best;
}
/** 이번 누름이 공격으로 끝났나 — 누른 채 있어도 끌기 이동으로 바뀌지 않게(끌어당긴 표적은 `pick` 이 다시 못 찾는다) */
let tapAttacked = false;

let siegeDownSaidAt = 0;
function onTap(px, py, force, touch = false) {
  if (!S.you) return;
  // 공성전에서 쓰러져 쉬는 동안은 서버가 걷기·대상을 받지 않는다(본진에 묶임) — 예측 걸음을 그렸다 되돌리지 않게 여기서 멈추고 까닭을 한 번 말한다
  const siegeDown = Number(S.you.siege?.dn) || 0;
  if (siegeDown > 0) {
    const t = performance.now();
    if (t - siegeDownSaidAt > 1500) { siegeDownSaidAt = t; log(`쓰러져 있다 — ${Math.ceil(siegeDown)}초 뒤 우리 본진에서 다시 선다. 그때까지 움직이지도 싸우지도 못한다.`, 'bad'); }
    return;
  }
  cancelTalk(); // 새로 누르면 앞의 「도착하면 열기」는 잊는다 — 아래 NPC 분기가 다시 걸 수 있다
  const w = screenToWorld(px, py);
  let hit = pick(w.x, w.y, force);
  if (!hit && touch && !safeGround()) hit = touchMonster(w.x, w.y);

  // A cave guard can stand inside the door's ground click area. A normal tap on
  // that ground means enter; forced guard attacks and every other target keep
  // their existing meaning. The server still decides entry when the body walks
  // within PORTAL_R of the actual mouth.
  if (!force && hit?.kind === 'guard' && !hit.o.bg && floorAt(S.you.x, S.you.y) === 0 && nativeFieldFloor() === 0) {
    const mouth = CAVE_MOUTHS.find((m) => Math.hypot(w.x - m.x, w.y - m.y) < PORTAL_R);
    if (mouth) {
      send({ t: 'move', x: mouth.x, y: mouth.y });
      noteTap(mouth.x, mouth.y, 'move');
      return;
    }
  }

  // UNI-L1 — 시간 슬롯 포탈은 서버가 좌표를 보낸다. 가까이 가서 누르면
  // 공지와 오늘의 사용 상태를 받고, 재료·승급 판정은 서버가 다시 한다.
  const smith = S.lair?.smith;
  // DESIGN 14.599 6단계 — 레어 층은 사냥터 서버 땅이다: 그 소켓도 대장장이 일을 받는다(어디서나 몸 행위 — 관문이 원천 규칙으로 판정 ·
  //   층·자리 판정은 아틀라스 좌표 `lairFloorAt`). 전에는 사냥터 소켓이면 누름을 안 보내 레어 층 대장장이에 닿지 못했다
  if (nativeActionAllowed('legendarySmith') && smith && lairFloorAt(w.x, w.y) === Number(smith.floor)
      && Math.hypot(w.x - Number(smith.x), w.y - Number(smith.y)) <= 3.5) {
    if (Math.hypot(S.you.x - Number(smith.x), S.you.y - Number(smith.y)) > 2.5) {
      send({ t: 'move', x: Number(smith.x), y: Number(smith.y) });
      // R207 — 도착하면 공지를 받는다(같은 층의 같은 대장장이일 때만)
      const sx = Number(smith.x), sy = Number(smith.y), sf = Number(smith.floor);
      const same = () => { const s = S.lair?.smith; return !!s && Number(s.floor) === sf && Number(s.x) === sx && Number(s.y) === sy; };
      queueTalk(() => same() && Math.hypot(S.you.x - sx, S.you.y - sy) <= 2.5,
        () => send({ t: 'legendarySmith', op: 'view' }), null);
    } else send({ t: 'legendarySmith', op: 'view' });
    return;
  }

  if (hit?.kind === 'resident') {
    S.residentId=hit.o.id; S.npcLine=null;
    window.dispatchEvent(new CustomEvent('unige:panel',{detail:'resident'}));
    return;
  }
  // R227 ① — 안전한 땅의 사람은 이름표 카드(거래·파티·귓말). 강공으로 눌러도 같다 — 거래는 강공 몸짓에서 떼어 냈다.
  // 강공으로 누른 원주민은 원주민 카드로(일반 탭과 같은 곳 — 안전한 땅에서는 칠 수 없다)
  if (hit?.kind === 'player' && safeGround() && (hit.o.resident || String(hit.o.id).startsWith('wanderer:'))) {
    S.residentId = hit.o.id; S.npcLine = null;
    window.dispatchEvent(new CustomEvent('unige:panel', { detail: 'resident' }));
    return;
  }
  if (hit?.kind === 'person' || (hit?.kind === 'player' && safeGround())) { openPersonCard(hit.o); return; }

  if (hit && hit.kind === 'keepStairs') {
    if(Math.hypot(S.you.x-KEEP_STAIRS.x,S.you.y-KEEP_STAIRS.y)>1.5)send({t:'move',x:KEEP_STAIRS.x,y:KEEP_STAIRS.y});
    else send({t:'keepStairs'});return;
  }
  if (hit && hit.kind === 'npc') {
    const nx = TOWN_X + hit.o.dx, ny = TOWN_Y + hit.o.dy;
    const estate=guildEstateAt(nx,ny);
    if(estate && S.you.guildEstateId!==estate.id) {
      const at=guildEstateOutside(estate);send({t:'move',x:at.x,y:at.y});toast('이 영지의 길드원만 성문을 통과할 수 있다.','trade');return;
    }
    const home = settlementAt(nx, ny, 2);
    const here = settlementAt(S.you.x, S.you.y, 2);
    // The king, guards and priest are visible behind the sealed gate but are
    // not human interaction targets. Do not send their inner approach point:
    // that used to make the server roll the player back and cancel movement,
    // which looked like the character had frozen near the village.
    // 2026-10-25 — 운영자(`you.admin`)는 성 안까지 들어가므로 왕·경비에게도 보통 NPC처럼 다가간다(서버 movement.ts·러스트 같은 규칙)
    if (home?.id === NATIVE_VILLAGE.id && hit.o.id !== 'native_market' && !S.you.admin) {
      const gate = nativeVillageGateOutside();
      if (Math.hypot(S.you.x - gate.x, S.you.y - gate.y) > 1.5) {
        send({ t: 'move', x: gate.x, y: gate.y });
      } else {
        toast('원주민 왕도는 성문 밖에서만 지켜볼 수 있다.', 'trade');
      }
      return;
    }
    /**
     * 대화 반경 3.2가 마을 판정 여유보다 넓다. 운장수 자락은 남쪽 끝에 있어서
     * 바깥에서도 패널이 열렸고 버튼은 「마을 밖」으로 막혔다. NPC와 같은 정착지에
     * 들어오기 전에는 열지 않고, `y + 1.2`가 아니라 정착지 중심 쪽으로 다가간다.
     */
    const npc = hit.o;
    const talk = () => { S.npcLine = npc; window.dispatchEvent(new CustomEvent('unige:panel', { detail: npc.panel })); };
    if (Math.hypot(nx - S.you.x, ny - S.you.y) > NPC_TALK_RANGE || (home && here?.id !== home.id)) {
      const at = npcApproachPoint(nx, ny);
      send({ t: 'move', x: at.x, y: at.y });
      queueTalk(() => talkReach(nx, ny, false), talk, npc.panel); // R207 — 도착하면 연다
      return;
    }
    talk();
    return;
  }
  if (hit && hit.kind === 'goblin') {
    // NPC 와 같은 접근 규칙 — 같은 정착지 안 3.2타일에서만 창이 열린다.
    const home = settlementAt(hit.o.x, hit.o.y, 2);
    const here = settlementAt(S.you.x, S.you.y, 2);
    const talk = () => { S.npcLine = null; window.dispatchEvent(new CustomEvent('unige:panel', { detail: 'goblin' })); };
    if (Math.hypot(hit.o.x - S.you.x, hit.o.y - S.you.y) > NPC_TALK_RANGE || (home && here?.id !== home.id)) {
      const at = npcApproachPoint(hit.o.x, hit.o.y);
      send({ t: 'move', x: at.x, y: at.y });
      // R207 — 도착하면 연다. 고블린은 잠깐만 서 있으니, 그사이 떠났으면(다른 방문) 열지 않는다
      const gx = hit.o.x, gy = hit.o.y, visit = hit.o.startSec;
      queueTalk(() => goblinAt(Date.now() / 1000)?.startSec === visit && talkReach(gx, gy, false), talk, 'goblin');
      return;
    }
    talk();
    return;
  }
  if (hit && hit.kind === 'board') {
    const bx = hit.o.x + hit.o.w / 2, by = hit.o.y + hit.o.h / 2;
    const home = settlementAt(bx, by, 2);
    const here = settlementAt(S.you.x, S.you.y, 2);
    const talk = () => { S.npcLine = null; window.dispatchEvent(new CustomEvent('unige:panel', { detail: 'board' })); };
    if (Math.hypot(bx - S.you.x, by - S.you.y) > NPC_TALK_RANGE || here?.id !== home?.id) {
      const at = npcApproachPoint(bx, by);
      send({ t: 'move', x: at.x, y: at.y });
      queueTalk(() => talkReach(bx, by, true), talk, 'board'); // R207 — 도착하면 연다
      return;
    }
    talk();
    return;
  }
  /**
   * 허수아비 — **한 번 누르면 서버가 접근해서 Lv5까지 반복 공격한다.**
   *
   * id는 의도만 보낸다. 서버가 정본 표와 현재 정착지를 다시 확인하고, 이동·사거리·
   * 타격 주기·경험치 상한을 전부 결정한다. 화면 타이머로 반복 전송하지 않는다.
   */
  if (hit && hit.kind === 'dummy') {
    send({ t: 'dummy', id: hit.o.id });
    return;
  }
  /**
   * UNI-Q161 샌드백 — **한 번 누르면 서버가 접근해서 계속 친다.**
   *
   * id 는 의도만 보낸다. 서버가 정본 표와 현재 마을을 다시 확인하고, 이동·사거리·
   * 타격 박자·피해·측정 창을 전부 결정한다. 화면 타이머가 측정값을 만들지 않는다.
   */
  if (hit && hit.kind === 'sandbag') {
    send({ t: 'sandbag', id: hit.o.id });
    return;
  }
  // Z1(평론 #1) — 누른 프레임에 표적 고리·파문·(사거리 안이면) 휘두름이 먼저 뜬다. 판정은 서버가 한다(combat-predict.js)
  if (hit && hit.kind === 'mon') { send({ t: 'attack', id: hit.o.id }); predictAttack(S.you, hit.o); tapAttacked = true; return; }
  if (hit && hit.kind === 'guard' && hit.o.bg) { send({ t: 'bodyguardHit', id: hit.o.id }); predictAttack(S.you, hit.o); tapAttacked = true; return; } // UNI-Q192 — 남의 보디가드(강공)
  if (hit && hit.kind === 'guard') {
    // GUARD-R2 §4 — 1💀 이 가까이서 누르면 흥정(유예), 그 외(또는 강공)는 공격.
    // 유예가 이미 걸려 있으면 HUD 확인띠([낸다]/[관둔다])가 결정한다 — 재클릭은 공격이 아니다.
    const near = Math.hypot(hit.o.x - S.you.x, hit.o.y - S.you.y) <= 2.5;
    if (!force && near && (S.you.crime | 0) === 1) {
      if (!hit.o.truce) send({ t: 'guardBribe', id: hit.o.id });
    } else { send({ t: 'guardHit', id: hit.o.id }); predictAttack(S.you, hit.o); tapAttacked = true; }
    return;
  }
  if (hit && hit.kind === 'player') {
    /**
     * **강공은 공격 하나만 뜻한다**(R227 ①). 전에는 「마을에서는 거래, 밖에서는 PK」였다 — 마을에서 `attackPlayer` 는 서버가
     * 어차피 거절하니 그 빈자리를 거래가 썼는데, 한 몸짓이 자리에 따라 두 뜻이 되자 거래하던 손이 사냥터에서 파티원을 쳤다
     * (사용자가 먼저 찾아냈다: *"원래는 컨트롤은 강제 공격 아니었나??"*). 안전한 땅의 사람은 위에서 이름표 카드로 갔다 —
     * 여기 오는 것은 칠 수 있는 땅의 사람뿐이고, 칠 수 있는지는 서버가 다시 본다.
     */
    send({ t: 'attackPlayer', id: hit.o.id });
    predictAttack(S.you, hit.o); tapAttacked = true;
    return;
  }
  if (hit && hit.kind === 'drop') { send({ t: 'move', x: hit.o.x, y: hit.o.y }); noteTap(hit.o.x, hit.o.y, 'move'); return; }
  send({ t: 'move', x: w.x, y: w.y });
  noteTap(w.x, w.y, 'move');
}

/*
 * `targetNearest()` 를 지웠다 — 주석이 스스로 "지금은 안 쓴다" 고 적어둔 채
 * `eslint-disable no-unused-vars` 로 경고까지 막아두고 남아 있었다.
 * 이 레포의 대표 결함("선언했는데 아무도 안 부르는 것")의 가장 노골적인 형태다.
 * 필요해지면 그때 쓰는 자리와 함께 되살린다.
 */
export function setForce(on) {
  S.forceMode = on;
  const b = $('#b-force');
  // 묶음 AB — 「강공」 만으로는 무엇인지 몰랐다. 아래 작은 줄이 뜻을 말한다(누르기가 곧 공격)
  if (b) { b.classList.toggle('on', on); b.innerHTML = on ? '강공<b class="cnt">ON</b><small>누르면 곧 공격</small>' : `강공<i class="kk">${keyCap(ACTION_KEYS.force)}</i><small>누르면 곧 공격</small>`; }
  document.body.classList.toggle('force', on);
}

/** R227 ① — 이름표 카드를 연다(`ui/panels/person.js`). 누른 사람을 기억하고, 카드가 시야·자리를 다시 본다 */
function openPersonCard(p) {
  S.personId = p.id; S.npcLine = null;
  window.dispatchEvent(new CustomEvent('unige:panel', { detail: 'person' }));
}

/**
 * R227 ② — **강공은 안전한 땅에 들어오면 꺼지고, 켠 채 나가면 한 줄로 말한다.** 전에는 버튼·A 키로만 바뀌는 고정 토글이라
 * 마을에서 켠 강공이 사냥터까지 따라갔고, 켜짐 표시는 붉은 테두리·십자 커서뿐이었다(WASD 습관이면 A 로 모르는 사이에 켜진다).
 * 상태가 올 때마다 본다 — 섬 소켓의 마을 드나듦과 사냥터로 넘어가는 것(사냥터 소켓 위는 늘 안전하지 않은 땅) 모두 여기서 걸린다.
 * 이 탭의 첫 상태에서는 말하지 않는다 — 새 탭의 강공은 꺼진 채 시작한다.
 */
let wasSafe = null;
export function syncForceGround(y = S.you) {
  if (!y) { wasSafe = null; return; }
  const safe = safeGround(y);
  if (wasSafe !== null && safe !== wasSafe && S.forceMode) {
    if (safe) { setForce(false); log('안전한 땅이라 강공을 껐다 — 여기서 사람을 누르면 이름표 카드(거래·파티)가 뜬다.', 'info'); }
    else log('강공 켜짐 — 사람을 누르면 공격한다.', 'bad');
  }
  wasSafe = safe;
}

function uiKeyTarget(target) {
  return !!(target?.isContentEditable || target?.closest?.('input,textarea,select,button,summary,a[href],[role="button"],[contenteditable]'));
}

/**
 * R204(레드팀 D2-5) — 게임 단축키로 읽을 **키 이름**. 첫 화면에서 한글 이름을 치고 들어오면 입력기가 한글 상태로 남아
 * `e.key` 가 자모(「ㅂ」)나 `Process` 로 와서 Q(물약)·R(귀환)·I(가방)·A(강공) 같은 글자 키가 **소리 없이 안 먹었다**
 * (숫자·Enter·Tab·Esc 만 됐다). 글자·숫자가 제대로 오면 그대로 쓰고(AZERTY 같은 다른 배열은 적힌 글자를 따른다),
 * 아니면 물리 키(`e.code` — `KeyQ`·`Digit1`)로 읽는다. 한/영 상태와 상관없이 같은 키가 같은 일을 한다.
 */
export function gameKey(e) {
  const key = String(e?.key ?? '');
  if (/^[a-z0-9]$/i.test(key)) return key.toLowerCase();
  const code = String(e?.code ?? '');
  if (/^Key[A-Z]$/.test(code)) return code.slice(3).toLowerCase();
  if (/^(?:Digit|Numpad)[0-9]$/.test(code)) return code.slice(-1);
  return key.toLowerCase();
}

/**
 * 키를 받은 칸이 **화면에서 사라졌으면**(첫 화면 이름 칸 `#nm`·시작 버튼에 포커스가 남은 채 게임으로 넘어온 경우)
 * 그 칸은 사람이 치고 있는 곳이 아니다 — 포커스를 풀고 단축키로 받는다. 전에는 Enter 로 입장하면 숨은 이름 칸이
 * 키를 계속 삼켜, 바닥을 한 번 누르기 전까지 단축키가 전부 죽어 있을 수 있었다.
 */
function hiddenKeyTarget(target) {
  if (!target || target === document.body || typeof target.getClientRects !== 'function') return false;
  return target.getClientRects().length === 0;
}

export function initInput(canvas) {
  cv = canvas;
  let dragging = false, lastDrag = 0;
  on('state', checkPendingTalk); // R207 — 먼 NPC 에 닿았는지는 서버 상태(내 위치)가 올 때마다 본다
  // R227 ② — 안전한 땅을 드나들 때 강공을 끄거나 켜져 있다고 말한다. 사냥터로 넘어갈 때의 `init` 에서 되돌리지 않는다 —
  // 그 순간이 바로 「켠 채 마을을 나가는」 때다(되돌리면 사냥터 첫 상태가 드나듦으로 안 읽힌다).
  on('state', () => syncForceGround());

  const isForce = (e) => S.forceMode || e.ctrlKey || e.shiftKey;

  // 우클릭은 `contextmenu` 로만 받는다 — `pointerdown` 에서도 보내면 한 번 누를 때
  // `untarget` 이 두 번 나간다(브라우저가 둘 다 발생시킨다)
  /**
   * R212(레드팀 D2-20) — 안드로이드 크롬은 **손가락으로 길게 누르기**에도 `contextmenu` 를 보낸다. 몬스터를 꾹 누르고 있으면
   * (모바일 액션 게임 습관) 반 초 뒤 공격 대상이 풀렸다. 대상 해제는 마우스 우클릭일 때만 — 손가락·펜은 메뉴만 막는다.
   */
  let lastPointerType = 'mouse';
  cv.addEventListener('contextmenu', (e) => {
    e.preventDefault();
    const type = e.pointerType || lastPointerType;
    if (type === 'touch' || type === 'pen') return;
    send({ t: 'untarget' });
  });

  /** 누른 자리 — 여기서 얼마나 멀어졌는지로 "끄는 중" 을 판정한다 */
  let downAt = null, moved = false;
  /** 마지막 포인터 화면 좌표 — 누른 채 가만히 있어도 이어 보내려면 이것이 있어야 한다 */
  let lastPx = 0, lastPy = 0, holdTimer = 0, pausedCrossing = null;
  /** MV1 — 누른 시각·화면 자리, 누르고 걷는 중인가, 마지막으로 보낸 걷는 방향(월드 단위 벡터) */
  let downTime = 0, downPx = 0, downPy = 0, holding = false, holdDir = null;

  /**
   * 끄는 중 한 걸음 보내기. **`pointermove` 와 유지 타이머가 같은 함수를 부른다.**
   *
   * 전에는 `pointermove` 안에서만 보냈다. 그래서 **누른 채 손가락을 멈추면 이동이 끊겼다** —
   * 이벤트가 안 나기 때문이다. 카메라는 나를 따라 움직이므로 같은 화면 점 아래의 월드 좌표는
   * 계속 앞으로 나아가는데, 그걸 읽는 사람이 없어서 처음 누른 한 점에서 섰다.
   * (2026-09-16 운영 신고: *"클릭하고 누르고 있어도 움직임 멈춰서 다시 재클릭 해야 움직이는게 자주"*)
   */
  const dragStep = (force) => {
    if (!dragging || pausedCrossing !== null || !S.you || Number(S.you.siege?.dn) > 0) return; // 공성전에서 쓰러져 쉬는 동안은 누르고 걷기도 없다(onTap 과 같은 문)
    const w = screenToWorld(lastPx, lastPy);
    /**
     * **끌기 시작하면 hover 가 이동을 막지 않는다.**
     *
     * 전에는 `!S.hover` 를 조건에 걸어서, 손가락(또는 커서)이 몬스터 위를 지나가는
     * 순간 이동이 **끊겼다.** 몹이 깔린 사냥터에서 끌어서 이동하는 것이 거의 불가능했고,
     * 모바일에서는 그게 유일한 이동 수단이라 더 나빴다.
     * 누른 자리에서 0.6타일 넘게 움직였으면 그건 **이동 의도**다 — 위에 뭐가 있든.
     */
    if (!moved && downAt && Math.hypot(w.x - downAt.x, w.y - downAt.y) > 0.6) moved = true;
    if (!moved && (tapAttacked || pick(w.x, w.y, force))) return; // 아직 제자리 — 누른 대상을 존중한다(손가락으로 끌어당긴 표적 포함)
    const now = performance.now();
    // MV1 — 짧게 누르고 떼면 탭이다(누른 자리는 onTap 이 이미 보냈다). 길게 누르거나 화면에서 끌면 그때부터 「누르고 걷기」다
    if (!holding && now - downTime < HOLD_MS && Math.hypot(lastPx - downPx, lastPy - downPy) <= DRAG_PX) return;
    holding = true;
    if (now - lastDrag > 90) { lastDrag = now; cancelTalk(); send({ t: 'move', ...holdTarget(w) }); } // 끌어서 딴 데로 가면 「도착하면 열기」도 잊는다
  };
  /**
   * MV1(DESIGN 14.469 — 사용자 실보고 「왜 우왕좌왕하냐?」) — **누르고 걷는 동안의 목표는 커서 방향으로 내 몸 앞 `DRAG_LEAD` 칸 이상.**
   * 전에는 커서 아래 월드 점을 그대로 보냈다. 카메라는 내 몸을 0.084초 늦게 따라가 걷는 동안 몸이 화면 가운데보다 0.35칸 앞에 서므로,
   * 커서를 몸 가까이(1칸 안) 대고 있으면 그 점이 몸 바로 앞이거나 **뒤**가 됐다 — 몸이 닿아 서고(카메라가 따라와 점이 다시 앞으로) 다시
   * 걷거나, 서버가 이미 지나친 점으로 **되돌아 걸었다**(톡톡 끊김·앞뒤 돌기). 이제 방향은 화면 가운데 → 커서(카메라 지연과 상관없다),
   * 거리는 커서까지와 `DRAG_LEAD` 중 큰 쪽이라 누르는 동안 몸이 목표에 닿지 않고 이어 걷는다. 커서가 몸 위(`DRAG_DEAD` 안)면 선다.
   * 손을 떼면 뗀 자리(아래 `releaseHold`)로 — 이미 지나쳤으면 지금 자리에 선다.
   */
  const holdTarget = (w) => {
    const you = S.you, bx = you.viewX ?? you.x, by = you.viewY ?? you.y;
    const c = screenToWorld(innerWidth / 2, innerHeight / 2);
    const ox = w.x - c.x, oy = w.y - c.y, d = Math.hypot(ox, oy);
    if (d < DRAG_DEAD) { holdDir = null; return { x: bx, y: by }; }
    holdDir = { x: ox / d, y: oy / d };
    const reach = Math.max(d, DRAG_LEAD);
    return { x: bx + holdDir.x * reach, y: by + holdDir.y * reach };
  };

  cv.addEventListener('pointerdown', (e) => {
    lastPointerType = e.pointerType || 'mouse';
    // Returning to the world ends a control's keyboard focus; game shortcuts work again.
    if (uiKeyTarget(document.activeElement)) document.activeElement.blur();
    e.preventDefault();
    if (e.button === 2) return; // contextmenu 가 처리한다
    if (overKeepVista(e.clientX,e.clientY)) { dragging=false;downAt=null;return; }
    dragging = true; moved = false; holding = false; holdDir = null; pausedCrossing = null;
    lastPx = e.clientX; lastPy = e.clientY;
    downTime = performance.now(); downPx = e.clientX; downPy = e.clientY;
    downAt = screenToWorld(e.clientX, e.clientY);
    tapAttacked = false;
    onTap(e.clientX, e.clientY, isForce(e), e.pointerType === 'touch' || e.pointerType === 'pen');
    // 손가락이 멈춰 있어도 이어 간다 — 보내는 간격은 dragStep 안의 90ms 문이 정한다
    clearInterval(holdTimer);
    holdTimer = setInterval(() => {
      /**
       * **타이머는 스스로 멈춘다.** 손 뗌(pointerup)은 늘 오지 않는다 — 누른 채 다른 창·앱으로 넘어가면
       * 영영 안 와서, 타이머가 돌며 캐릭터가 **끝없이 걸어갔다**(검사 시험 환경에서는 프로세스가 끝나지 않았다).
       * 누르는 중이 아니거나 창이 포커스를 잃었으면 끈다.
       */
      if (!dragging || document.hasFocus?.() === false) { endDrag(); return; }
      dragStep(S.forceMode);
    }, 60);
    holdTimer?.unref?.(); // 브라우저 밖(검사 시험 환경 Node)에서는 이 타이머가 프로세스를 붙잡지 않게 한다
  });
  const endDrag = () => {
    clearInterval(holdTimer); holdTimer = 0;
    dragging = false; downAt = null; moved = false; holding = false; holdDir = null; pausedCrossing = null;
    // 터치는 손을 떼도 마지막 hover 가 남아 강조·툴팁이 고착된다 — 뗄 때 지운다
    if (S.hover && S.hover.touch) S.hover = null;
  };
  /*
   * 14.513(F1, 사용자 원문 2026-10-06 「모바일에서 맵 넘어갈때 안 움직이면 포탈 계속 타서 재접해야 된다」) — 좌표가 바뀌는 넘기(마을 관문 ↔ 아틀라스
   * 사냥터·갱도와 층·옮겨진 귀환)에서는 누르고 있던 손짓을 끝낸다(`net.js` `forgetCrossingMotion`). 옛 판에서 시작한 손짓의 손 뗌 이동(`releaseHold`)이
   * 새 판에 옛 좌표로 나가지 않는다 — 손을 떼고 다시 누르면 새로 걷는다(사람의 새 입력은 그대로 받는다). 같은 판의 지상 넘기는 손짓을 그대로 잇는다.
   */
  on('crossingMotionReset', () => { if (dragging) endDrag(); });
  on('crossingMotionPause', ({ epoch }) => {
    if (!dragging) return;
    if (!holding) { endDrag(); return; }
    pausedCrossing = epoch;
  });
  on('crossingMotionResume', ({ epoch }) => {
    if (!dragging || pausedCrossing !== epoch) return;
    if (document.hasFocus?.() === false) { endDrag(); return; }
    pausedCrossing = null;
    lastDrag = 0;
    dragStep(S.forceMode); // current pointer and current camera, never the former region's target
  });
  /*
   * MV1 — 누르고 걷다가 손을 떼면: 뗀 자리가 걷던 방향 앞이면 그리로 가서 서고, 이미 지나쳤으면 지금 자리에 선다(뒤로 돌아가지 않는다).
   * 터치가 취소되면(브라우저가 손짓을 가져감) 그 자리에 선다. 탭(짧게 누름)은 onTap 이 보낸 누른 자리 그대로다.
   */
  const releaseHold = (e) => {
    if (!dragging || pausedCrossing !== null || !holding || !S.you || Number(S.you.siege?.dn) > 0) return;
    const bx = S.you.viewX ?? S.you.x, by = S.you.viewY ?? S.you.y;
    const w = e.type === 'pointerup' ? screenToWorld(lastPx, lastPy) : null;
    const ahead = w && holdDir && (w.x - bx) * holdDir.x + (w.y - by) * holdDir.y > 0;
    send({ t: 'move', x: ahead ? w.x : bx, y: ahead ? w.y : by });
  };
  addEventListener('pointerup', (e) => { releaseHold(e); endDrag(); });
  addEventListener('pointercancel', (e) => { releaseHold(e); endDrag(); });

  cv.addEventListener('pointermove', (e) => {
    lastPx = e.clientX; lastPy = e.clientY;
    if(overKeepVista(e.clientX,e.clientY)){S.hover=null;cv.style.cursor='default';return;}
    const w = screenToWorld(e.clientX, e.clientY);
    const touch = e.pointerType === 'touch';
    const hit = pick(w.x, w.y, isForce(e));
    S.hover = hit ? { ...hit, touch } : null;
    // R227 ③ — 강공이어도 누를 수 있는 NPC·게시판·이름표 카드 위에서는 손가락 모양이다(칠 것 위에서만 조준선)
    const aims = !hit || hit.kind === 'player' || hit.kind === 'mon' || hit.kind === 'guard';
    cv.style.cursor = isForce(e) && aims ? 'crosshair' : hit ? 'pointer' : 'crosshair';

    // 창 밖에서 버튼을 떼면 손 뗌이 안 온다 — 돌아와 움직일 때 버튼이 이미 떨어져 있으면 끝낸다
    if (dragging && e.pointerType === 'mouse' && e.buttons === 0) { endDrag(); return; }
    dragStep(isForce(e));
  });

  /**
   * R204 — **마우스·손가락으로 누른 버튼은 포커스를 놓는다.** 크롬은 누른 버튼에 포커스를 남기고, 포커스가 버튼에 있으면
   * 단축키는 그 버튼 몫이라(키보드로 버튼을 고르는 사람의 칸 이동을 지키는 규칙) 하단 「가방」을 누른 뒤 Q·R 이 바닥을 한 번
   * 누르기 전까지 안 먹었다. 키보드(Enter·Space)로 누른 것은 `detail` 이 0 이라 그대로 둔다 — 그 규칙은 안 바뀐다.
   */
  addEventListener('click', (e) => {
    if (!e.detail) return;
    const b = e.target?.closest?.('button,summary,a[href],[role="button"]');
    if (b && document.activeElement === b && !b.closest('dialog[open],#adminview')) b.blur();
  });

  addEventListener('keydown', (e) => {
    if (e.defaultPrevented) return;
    if (uiKeyTarget(e.target)) {
      if (!hiddenKeyTarget(e.target)) return; // 칸·버튼이 키를 가진다(키보드로 고른 버튼 포함 — 한글 조합 중도 여기서 걸린다)
      e.target.blur?.();
    } else if (e.isComposing) return;
    // Ctrl·Alt·⌘ 조합은 브라우저 몫이다 — Ctrl+R(새로고침)이 귀환 주문서를 쓰고 Ctrl+F(찾기)가 강화 창을 열던 자리
    if (e.ctrlKey || e.altKey || e.metaKey) return;
    const k = gameKey(e);
    /**
     * R141(레드팀 H13) — **꾹 누른 키의 자동 반복은 버린다.** 키는 전부 「한 번 누르면 한 번」인 행동이라
     * 반복을 받으면 스킬 키가 초당 30번씩 사냥터로 나가 입력 한도를 넘겨 소켓이 닫히고, Q 를 누르고 있으면
     * 물약이 연달아 사라지고, 패널 키는 열렸다 닫혔다를 되풀이했다. 반복이 필요한 키는 keymap.js 표에만 넣는다.
     */
    if (e.repeat && !REPEATABLE_KEYS.has(k)) return;
    if (k === 'a') setForce(!S.forceMode);
    else if (k === 'tab') { e.preventDefault(); window.dispatchEvent(new CustomEvent('unige:key', { detail: 'i' })); }
    else window.dispatchEvent(new CustomEvent('unige:key', { detail: k }));
  });
}
